import { CardCvcElement } from '@stripe/react-stripe-js'
import { StripeCardCvcElementOptions } from '@stripe/stripe-js'
import { useCheckoutContext } from 'checkout/containers/CheckoutContextProvider'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { t } from 'localization'
import sendTrackingEvent from 'mParticle/sendTrackingEvent'
import { useEffect, useState } from 'react'
import { Button } from 'shared/components/Button'
import Divider from 'shared/components/Divider'
import { CardBrand } from 'shared/components/Icons/PaymentIcons/CardIcon/CardIcon'
import Spinner from 'shared/components/Spinner'
import { CheckoutQA } from 'shared/dataAttributes'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import PaymentType from 'shared/enums/PaymentType'
import useCurrency from 'shared/hooks/useCurrency'
import { colors, fonts } from 'shared/lib'
import styled from 'styled-components'
import AddressSummaryDisplay from '../ShippingAddressPanel/AddressSummaryDisplay'
import PaymentSummary from './PaymentSummary'

const TP = 'checkout.components.PaymentMethodPanel.CreditCardVerification'

// styles
const CardAddressSummary = styled(AddressSummaryDisplay)`
  margin: 25px 0 0;
`
const VerificationForm = styled.div`
  background-color: ${colors.FC2_OFF_WHITE};
  padding: 30px;
  margin: 30px 0;
`
const Title = styled.h3`
  ${fonts.SUBTITLE_1}
  font-weight: normal;
  margin: 0;
`
const Subtitle = styled.h5`
  ${fonts.SUBTITLE_3}
  color: ${colors.FC2_GREY};
  margin: 10px 0 5px;
`
const ButtonGroup = styled.div`
  display: flex;
  align-items: flex-end;
`
const SubmitButton = styled(Button)`
  width: 170px;
  margin-left: 15px;
`
const CardCvcWrapper = styled.div`
  width: 148px;
  padding: 7px 11px;
  background-color: white;
  border: 1px solid ${colors.FC2_DARK_GREY};
`
const cvcElementOptions: StripeCardCvcElementOptions = {
  style: {
    base: {
      backgroundColor: '#ffffff',
      fontFamily: 'Helvetica Neue',
      fontSize: '12px',
      fontWeight: '500',
      color: '#000000',
      textAlign: 'center',
      lineHeight: '29px',
    },
  },
}

const CreditCardVerification = () => {
  const isCreditCardVerificationEnabled = useFeatureFlag(FeatureFlag.CREDIT_CARD_VERIFICATION)
  const [codeEntered, setCodeEntered] = useState(false)
  const { order, panels, verifyCreditCard } = useCheckoutContext()
  const { billingInfo, billingAddress } = order || {}
  const { selectedCurrency } = useCurrency()

  useEffect(() => {
    if (billingInfo?.id && isCreditCardVerificationEnabled) {
      sendTrackingEvent('CHECKOUT_CVV_MODAL_VIEW', {
        product_template_slug: order?.product.productTemplate.slug,
        size: order?.product.size.value,
        price: order?.productPrice.value,
        condition: order?.product.shoeCondition,
        instant_ship: order?.isInstantShip,
        is_apparel_product: order?.product?.productTemplate?.productCategory !== 'shoes',
        currency_code: selectedCurrency?.isoCode,
      })
    }
  }, [billingInfo?.id, isCreditCardVerificationEnabled])

  if (!billingInfo || !isCreditCardVerificationEnabled) {
    return null
  }

  // We have no way of controlling 3 digit vs 4 digit CVC because the Stripe API doesn't allow it
  // https://github.com/stripe/react-stripe-elements/issues/242
  // This only changes the placeholder to 4 dots and modifies the copy on the page for Amex
  // @ts-ignore
  const isAmex = billingInfo?.cardBrand === CardBrand.AmericanExpress

  return (
    <Spinner
      showSpinner={panels.paymentMethod.loading}
      text={t(`${TP}.spinnerText`, 'Verifying your credit card...')}
      subText={t(`${TP}.spinnerSubText`, 'Please do not close your browser!')}
    >
      <PaymentSummary billingInfo={billingInfo} />
      <VerificationForm>
        <Title data-qa={CheckoutQA.CvvTitleHeader}>
          {t(`${TP}.verifyCVC`, 'Please verify your CVC')}
        </Title>
        <p data-qa={CheckoutQA.CvvDescriptionText}>
          {t(
            `${TP}.cvvDescription`,
            'A CVC verification helps prevent unauthorized use of your account.',
          )}{' '}
          {isAmex
            ? t(
                `${TP}.cvcFourDigit`,
                'Your CVC is the 4-digit code that can be found on the front of your card.',
              )
            : t(
                `${TP}.cvcTreeDigit`,
                'Your CVC is the 3-digit code that can be found on the back of your card.',
              )}
        </p>
        <ButtonGroup>
          <div>
            <Subtitle data-qa={CheckoutQA.CvvInputBoxName}>{t(`${TP}.cvc`, 'CVC')}</Subtitle>
            <CardCvcWrapper>
              <CardCvcElement
                onChange={({ complete }) => setCodeEntered(complete)}
                options={Object.assign({}, cvcElementOptions, {
                  placeholder: isAmex ? '• • • •' : '• • •',
                })}
              />
            </CardCvcWrapper>
          </div>
          <SubmitButton
            buttonType="primary1"
            disabled={codeEntered === false}
            onClick={verifyCreditCard}
            type="button"
            $fill
            qaAttr={CheckoutQA.CvvSubmitButton}
          >
            {t(`${TP}.submit`, 'Submit')}
          </SubmitButton>
        </ButtonGroup>
      </VerificationForm>
      {billingInfo.paymentType === PaymentType.CreditCard && !!billingAddress && (
        <>
          <Divider />
          <CardAddressSummary
            qaAttr="BillingAddress"
            name={billingAddress.name}
            address1={billingAddress.address1}
            address2={billingAddress.address2}
            city={billingAddress.city}
            state={billingAddress.state}
            postalCode={billingAddress.postalCode || ''}
            country={billingAddress.countryCode}
            phone={billingAddress.phone}
          />
        </>
      )}
    </Spinner>
  )
}

export default CreditCardVerification
